import api from './api'
import { saveBlob } from '../../lib/download'

/**
 * PURCHASE ORDERS — the documents a customer or a reseller sends TO XOP
 * (buyer → XOP, never the other way). crm-api owns them; this is the command
 * center's client for `/api/v1/purchase_orders*`. Who crm-api mails about them
 * is System Admin ▸ System Emails ("Purchase orders"), not this client.
 *
 * Three sources, one row shape:
 *   - `quote`    — a reseller accepted an XOP quote and pressed "Create purchase
 *                  order" on its portal (lines + prices copied from the quote);
 *   - `reseller` — a reseller uploaded its own PO on its portal;
 *   - `upload`   — XOP staff uploaded one that arrived by email.
 *
 * Money is integer CENTS on every field. The walls are applied by crm-api (a
 * token without `crm_financials` gets no `*_cents` keys; without
 * `crm_resellers` no reseller keys) and AGAIN in the SPA through `<Money>` /
 * `<ResellerOnly>` — this client never formats anything.
 *
 * Contract: docs/crm.md → "Purchase Orders".
 */

export type PurchaseOrderStatus = 'received' | 'accepted' | 'cancelled'
export type PurchaseOrderSource = 'quote' | 'reseller' | 'upload'

export interface PurchaseOrderLine {
  part_number?: string | null
  description?: string | null
  unit?: string | null
  quantity: number
  /** Absent for a viewer without financial access. */
  unit_price_cents?: number | null
  line_total_cents?: number | null
}

export interface PurchaseOrderParty {
  name?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  email?: string | null
}

export interface PurchaseOrder {
  id: number
  /** XOP's own record number, e.g. `PO-2026-0007`. Always present. */
  number: string
  /** The BUYER's PO number; null until someone enters it. */
  po_number: string | null
  source: PurchaseOrderSource
  status: PurchaseOrderStatus
  order_date: string | null
  currency: string
  lines: PurchaseOrderLine[]
  subtotal_cents?: number | null
  total_cents?: number | null
  customer_id: number | null
  customer: { id: number; company_name: string } | null
  reseller_id?: number | null
  reseller?: { id: number; name: string; code?: string | null } | null
  reseller_quote?: { id: number; number: string } | null
  buyer: PurchaseOrderParty | null
  ship_to: PurchaseOrderParty | null
  note: string | null
  submitted_by_email: string | null
  created_by?: { id: number; full_name: string } | null
  has_document: boolean
  document: { filename: string; content_type: string; byte_size: number } | null
  has_pdf: boolean
  /** The server's verdict for THIS actor — never re-derive it client-side. */
  editable: boolean
  /**
   * The server's verdict on "may THIS actor cancel it": staff ⇒ not already
   * cancelled; a reseller ⇒ still `received`, not an XOP upload, and holding
   * `invoices:edit`. Optional only so a crm-api that has not shipped the field
   * yet still renders — when it is absent the page falls back to the staff
   * rule. Never compare the status yourself.
   */
  cancellable?: boolean
  /**
   * The cancellation stamp, written by crm-api WHEREVER an order becomes
   * cancelled (the reseller's own Cancel order on its portal, or staff setting
   * the status here) — one place, so the two can never disagree. `cancelled_by`
   * is a human label of WHO: "Acme Distribution · buyer@acme.test" for a
   * reseller, the staff user's name for staff. Absent on an order cancelled
   * before the stamp existed.
   */
  cancelled_at?: string | null
  cancelled_by?: string | null
  created_at: string
  updated_at: string
}

export type PurchaseOrderSort = 'number' | 'order_date' | 'total_cents' | 'status' | 'created_at'

export interface PurchaseOrderListParams {
  page?: number
  per_page?: number
  search?: string
  status?: PurchaseOrderStatus | ''
  source?: PurchaseOrderSource | ''
  customer_id?: number | string
  reseller_id?: number | string
  sort?: PurchaseOrderSort
  dir?: 'asc' | 'desc'
}

export interface PurchaseOrderPagination {
  current_page: number
  per_page: number
  total_pages: number
  total_count: number
  has_next: boolean
  has_prev: boolean
}

export interface PurchaseOrderListResponse {
  purchase_orders: PurchaseOrder[]
  meta: { pagination: PurchaseOrderPagination }
  counts: Record<PurchaseOrderStatus, number>
}

/** Fields a staff upload may carry beside the file. */
export interface PurchaseOrderCreateInput {
  po_number?: string | null
  order_date?: string | null
  customer_id?: number | string | null
  reseller_id?: number | string | null
  total_cents?: number | null
  currency?: string | null
  note?: string | null
}

export interface PurchaseOrderPatch extends PurchaseOrderCreateInput {
  status?: PurchaseOrderStatus
}

/** Build the query string, dropping blanks so an empty filter is not sent. */
export function purchaseOrderQuery(params: PurchaseOrderListParams = {}): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    query.append(key, String(value))
  }
  const text = query.toString()
  return text ? `?${text}` : ''
}

/** A download name that says what the file is: `PO-2026-0007.pdf`. */
export function purchaseOrderPdfFilename(po: Pick<PurchaseOrder, 'number' | 'id'>): string {
  return `${(po.number || `purchase-order-${po.id}`).replace(/[^\w.-]+/g, '_')}.pdf`
}

/** The buyer's own file keeps its own name; failing that, one built from the record number. */
export function purchaseOrderDocumentFilename(po: Pick<PurchaseOrder, 'number' | 'id' | 'document'>): string {
  return po.document?.filename || `${(po.number || `purchase-order-${po.id}`).replace(/[^\w.-]+/g, '_')}-document`
}

function appendField(form: FormData, key: string, value: unknown): void {
  if (value === undefined || value === null || value === '') return
  form.append(`purchase_order[${key}]`, String(value))
}

class PurchaseOrdersApi {
  async list(params: PurchaseOrderListParams = {}): Promise<PurchaseOrderListResponse> {
    const response = await api.get(`/purchase_orders${purchaseOrderQuery(params)}`)
    return response.data
  }

  async get(id: number | string): Promise<PurchaseOrder> {
    const response = await api.get(`/purchase_orders/${id}`)
    return response.data.purchase_order
  }

  /** Staff upload: the file is REQUIRED; at least one of customer / reseller is enforced by crm-api (and the dialog). */
  async create(fields: PurchaseOrderCreateInput, document: File): Promise<PurchaseOrder> {
    const form = new FormData()
    form.append('document', document)
    for (const [key, value] of Object.entries(fields)) appendField(form, key, value)
    const response = await api.post('/purchase_orders', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.purchase_order
  }

  async update(id: number | string, patch: PurchaseOrderPatch): Promise<PurchaseOrder> {
    const response = await api.patch(`/purchase_orders/${id}`, { purchase_order: patch })
    return response.data.purchase_order
  }

  /** Attach or replace the buyer's document. */
  async uploadDocument(id: number | string, document: File): Promise<PurchaseOrder> {
    const form = new FormData()
    form.append('document', document)
    const response = await api.post(`/purchase_orders/${id}/document`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.purchase_order
  }

  /**
   * Raw bytes for `<PdfReader>` / an image preview. `.slice(0)` is a COPY on
   * purpose: pdf.js DETACHES the buffer it is handed, and a view onto the
   * response buffer makes the second preview throw "Buffer is already
   * detached" (a crash this app has shipped once).
   */
  async documentBytes(id: number | string): Promise<ArrayBuffer> {
    const response = await api.get(`/purchase_orders/${id}/document?disposition=inline`, {
      responseType: 'arraybuffer',
    })
    return (response.data as ArrayBuffer).slice(0)
  }

  /** The standard PO PDF crm-api renders from the lines (404 `NO_LINES` when `has_pdf` is false). */
  async pdfBytes(id: number | string): Promise<ArrayBuffer> {
    const response = await api.get(`/purchase_orders/${id}/pdf`, { responseType: 'arraybuffer' })
    return (response.data as ArrayBuffer).slice(0)
  }

  async downloadPdf(po: Pick<PurchaseOrder, 'id' | 'number'>): Promise<void> {
    const response = await api.get(`/purchase_orders/${po.id}/pdf`, { responseType: 'blob' })
    await saveBlob(purchaseOrderPdfFilename(po), new Blob([response.data], { type: 'application/pdf' }))
  }

  async downloadDocument(po: Pick<PurchaseOrder, 'id' | 'number' | 'document'>): Promise<void> {
    const response = await api.get(`/purchase_orders/${po.id}/document?disposition=attachment`, {
      responseType: 'blob',
    })
    const type = po.document?.content_type
    await saveBlob(
      purchaseOrderDocumentFilename(po),
      new Blob([response.data], type ? { type } : undefined),
    )
  }

  /** Soft delete (staff only). */
  async remove(id: number | string): Promise<void> {
    await api.delete(`/purchase_orders/${id}`)
  }

}

export const purchaseOrdersApi = new PurchaseOrdersApi()
export default purchaseOrdersApi
